
































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { MIRA_ALL_CLIENTS_SCOPE, MIRA_PERIOD_ROLLING_12, MiraSection, MiraSectionDocument } from "@/data/models/Mira";
import { persistActiveUserMiraLandingFigures } from "@/data/repository/UserRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  COLOR_DARK_FILL_TEXT,
  COLOR_MIRA_TILE_SELECTED,
  COLOR_MIRA_TILE_SELECTED_DARK,
  COLOR_WHITE_TEXT,
} from "@/utils/colors";
import { collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraTile } from "@/utils/MiraDataQualityView";
import { MIRA_LANDING_FIGURES, cardAvailability, landingFigures, toggleFigureChoice } from "@/utils/MiraOverview";
import { MIRA_SECTION_TILES, miraViewContext } from "@/utils/MiraSectionTiles";
import { themed } from "@/utils/Theme";

/**
 * The settings of one Mira landing card (Hans, #2610102), opened from the card's gear and from the
 * "Was weitere Kennzahlen freischaltet" card on the screens.
 *
 * Shows what the section has in the landing card's period (the last 12 months from a screen): how many figures are
 * available and how many wait on data, every available figure with its place on the card, and what
 * unlocks the waiting ones. In edit mode a click picks the figures in the order they should stand on the
 * card, up to {@link MIRA_LANDING_FIGURES}, and the card shows only those (`landingFigures`). The
 * choice is the user's own, saved on their user record.
 *
 * `show()` resolves with the saved choice (`[]` for the default), or null when closed without saving.
 * Read-only (no BEARBEITEN) when `editable` is false: under Sicht auf, or without a user record.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class MiraLandingFiguresDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  isEditMode = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  section: MiraSection | null = null;
  sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};
  stored: string[] = [];
  choice: string[] = [];
  period: string = MIRA_PERIOD_ROLLING_12;

  private resolve: ((result: string[] | null) => void) | null = null;

  get document(): MiraSectionDocument | null {
    return this.section ? this.sections[this.section] ?? null : null;
  }

  /** The section's figures with a value in the landing page's period, in its screen's order. */
  get tiles(): MiraTile[] {
    const document = this.document;
    const section = this.section;
    const context = miraViewContext(this);
    if (!document || !section) {
      return [];
    }
    return MIRA_SECTION_TILES[section](document.periods[this.period] ?? null, null, context);
  }

  /** The card's figures as the current choice would draw them. */
  get cardIds(): string[] {
    return landingFigures(this.tiles, this.choice).map((tile) => tile.id);
  }

  get waitingIds(): string[] {
    const section = this.section;
    if (!section || this.document?.scope === MIRA_ALL_CLIENTS_SCOPE) {
      return [];
    }
    const only: Partial<Record<MiraSection, MiraSectionDocument>> = { [section]: this.document ?? undefined };
    return collectUnlocks(only, this.period)[0]?.metricIds ?? [];
  }

  get availabilityText(): string {
    if (!this.document) {
      return "";
    }
    const { available, waiting } = cardAvailability(this.tiles, this.document, this.period);
    const availableText = this.$tc("mira.landing.available", available).toString();
    return waiting > 0 ? `${availableText} · ${this.$tc("mira.landing.waiting", waiting)}` : availableText;
  }

  /** What decides the card: the hint to click in edit mode, else the choice in force. */
  get choiceNote(): string {
    if (this.isEditMode) {
      return this.$t("mira.landing.settings.hint", [MIRA_LANDING_FIGURES]).toString();
    }
    return this.$t(
      this.choice.length > 0 ? "mira.landing.settings.ownChoice" : "mira.landing.settings.defaultChoice",
      [MIRA_LANDING_FIGURES]
    ).toString();
  }

  get hasChanged(): boolean {
    const choice = this.choice;
    const stored = this.stored;
    return choice.length !== stored.length || choice.some((id, index) => id !== stored[index]);
  }

  /** The figure's place on the card, or nothing when it is not on it. */
  positionText(id: string): string {
    const index = this.cardIds.indexOf(id);
    return index >= 0 ? String(index + 1) : "";
  }

  /** A picked figure's place is filled; one the screen's order put there is only outlined. */
  positionBadgeClass(id: string): Record<string, boolean> {
    return { "position-badge--filled-in": !this.choice.includes(id) && this.cardIds.includes(id) };
  }

  /**
   * The tile-selected blue of the screens; a lighter blue with dark text in dark mode. While editing, a figure
   * not on the card shows an empty dotted circle, the place it could take, as in "Vergleiche verwalten"; none
   * once {@link MIRA_LANDING_FIGURES} are picked (Taimoor, 2026-11-03).
   */
  positionBadgeStyle(id: string): Record<string, string> {
    const color = themed(COLOR_MIRA_TILE_SELECTED, COLOR_MIRA_TILE_SELECTED_DARK);
    if (this.choice.includes(id)) {
      return { backgroundColor: color, borderColor: color, color: themed(COLOR_WHITE_TEXT, COLOR_DARK_FILL_TEXT) };
    }
    if (this.cardIds.includes(id)) {
      return { borderColor: color };
    }
    return this.isEditMode && this.choice.length < MIRA_LANDING_FIGURES ? { borderColor: color, borderStyle: "dashed" } : {};
  }

  metricTitle(metricId: string): string {
    const key = `mira.metrics.${metricId}.title`;
    return this.$te(key) ? this.$t(key).toString() : metricId;
  }

  metricUnlock(metricId: string): string {
    const key = `mira.metrics.${metricId}.unlock`;
    return this.$t(this.$te(key) ? key : "mira.dataQuality.unlocks.fallback").toString();
  }

  /**
   * @param section - the card's section.
   * @param sections - the loaded section documents.
   * @param chosenIds - the user's current choice for the section; empty for the default.
   * @param editable - whether BEARBEITEN is offered.
   * @param period - the landing page's period, so the list shows what the card shows (#2610137).
   */
  show(
    section: MiraSection,
    sections: Partial<Record<MiraSection, MiraSectionDocument>>,
    chosenIds: string[],
    editable: boolean,
    period: string = MIRA_PERIOD_ROLLING_12
  ): Promise<string[] | null> {
    this.section = section;
    this.period = period;
    this.sections = sections;
    this.stored = [...chosenIds];
    this.choice = [...chosenIds];
    this.isEditMode = false;

    this.baseDialog.show({
      title: this.titleText(),
      positiveButtonText: editable ? this.$t("settings.buttons.change").toString() : null,
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      scrollable: true,
      persistent: false,
      closeOnSave: false,
      closeOnNeutralPositiveAction: false,
    });
    // STANDARD belongs to edit mode only.
    this.baseDialog.showNeutralPositiveButton = false;

    return new Promise((resolve) => (this.resolve = resolve));
  }

  private titleText(): string {
    const name = this.section ? this.$t(`mira.sections.${this.section}`).toString() : "";
    return this.$t(this.isEditMode ? "mira.landing.settings.editTitle" : "mira.landing.settings.title", [name]).toString();
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    if (this.isEditMode) {
      this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
    }
  }

  onToggle(id: string) {
    this.choice = toggleFigureChoice(this.choice, id);
  }

  /** STANDARD: back to the screen's first figures; saved with SICHERN like any other change. */
  onReset() {
    this.choice = [];
  }

  /** BEARBEITEN in view mode, SICHERN in edit mode. */
  onPositive() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.baseDialog.title = this.titleText();
      this.baseDialog.positiveButtonText = this.$t("common.save").toString();
      this.baseDialog.neutralPositiveButtonText = this.$t("mira.landing.settings.reset").toString();
      this.baseDialog.showNeutralPositiveButton = true;
      this.baseDialog.persistent = true;
      return;
    }
    this.onSave();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.isEditMode || !this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged || !this.section) {
      return;
    }
    this.busy = true;
    try {
      const choice = [...this.choice];
      await persistActiveUserMiraLandingFigures(this.section, choice.length > 0 ? choice : null);
      SnackbarModule.showSuccess({ key: "mira.landing.settings.saved" });
      this.close(choice);
    } catch (error) {
      SnackbarModule.showError({ key: "mira.landing.settings.saveError" });
    } finally {
      this.busy = false;
    }
  }

  close(result: string[] | null) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  finish(result: string[] | null) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
